import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Globe, FileText, Briefcase, CheckCircle2, XCircle,
  TrendingUp, Search, Eye, MousePointer, BarChart3, Loader2, RefreshCw,
  Clock, MapPin, Home, Sun, Users
} from "lucide-react";

function StatusDot({ ok }) {
  return <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />;
}

function MetricCard({ icon: Icon, label, value, sub, color = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-xs text-slate-500 font-body">{label}</span>
      </div>
      <p className={`font-sans font-bold text-2xl ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 font-body mt-0.5">{sub}</p>}
    </div>
  );
}

export function SEODashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("overview"); // overview | opportunities | coverage

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/admin/seo/dashboard"); setData(res.data); }
    catch {} finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>;
  if (!data) return <p className="text-sm text-slate-500 font-body text-center py-8">Could not load SEO dashboard.</p>;

  const { coverage, landings, sitemap, indexing_api, search_console, opportunities } = data;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "opportunities", label: `Opportunities (${opportunities.length})` },
    { id: "coverage", label: "Coverage" },
  ];

  return (
    <div className="space-y-5" data-testid="seo-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="font-sans font-bold text-xl text-slate-900">SEO Dashboard</h2>
        <Button variant="outline" size="sm" onClick={load} className="text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setSection(tab.id)}
            className={`px-4 py-2 text-sm font-body border-b-2 transition-colors ${section === tab.id ? "border-emerald-500 text-emerald-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW ===== */}
      {section === "overview" && (
        <div className="space-y-5">
          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard icon={Briefcase} label="Active jobs" value={coverage.active_jobs} sub={`${coverage.total_jobs} total`} color="text-emerald-600" />
            <MetricCard icon={Globe} label="Indexable landings" value={landings.indexable} sub={`${landings.noindex} noindex`} />
            <MetricCard icon={FileText} label="Sitemap" value={sitemap.exists ? "OK" : "Missing"} color={sitemap.exists ? "text-emerald-600" : "text-red-600"} />
            <MetricCard icon={TrendingUp} label="Indexing API" value={indexing_api.configured ? "Connected" : "Pending"} sub={indexing_api.configured ? `${indexing_api.success} sent` : ""} color={indexing_api.configured ? "text-emerald-600" : "text-amber-600"} />
          </div>

          {/* Connections */}
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-5">
              <h3 className="font-sans font-semibold text-sm text-slate-900 mb-3">Connections</h3>
              <div className="space-y-2.5">
                {[
                  { ok: sitemap.exists, label: "Sitemap", status: sitemap.exists ? "Active" : "Missing" },
                  { ok: indexing_api.configured, label: "Google Indexing API", status: indexing_api.configured ? "Connected" : "Pending" },
                  { ok: search_console.configured, label: "Google Search Console", status: search_console.configured ? "Connected" : "Pending" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <StatusDot ok={item.ok} />
                    <span className="text-sm font-body text-slate-700">{item.label}</span>
                    <Badge className={`text-xs ml-auto ${item.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{item.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Search Console */}
          {search_console.configured && search_console.summary ? (
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-5">
                <h3 className="font-sans font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2"><Search className="w-4 h-4" /> Organic Performance</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard icon={MousePointer} label="Clicks" value={search_console.summary.clicks} color="text-sky-600" />
                  <MetricCard icon={Eye} label="Impressions" value={search_console.summary.impressions} />
                  <MetricCard icon={BarChart3} label="CTR" value={`${search_console.summary.ctr}%`} />
                  <MetricCard icon={TrendingUp} label="Avg. Position" value={search_console.summary.position} />
                </div>
                <p className="text-xs text-slate-400 font-body mt-2">{search_console.summary.period}</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-5 text-center">
                <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500 font-body">{search_console.configured ? "Waiting for Google to provide data..." : "Google Search Console pending setup."}</p>
              </CardContent>
            </Card>
          )}

          {/* Indexing API log */}
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-5">
              <h3 className="font-sans font-semibold text-sm text-slate-900 mb-3">Indexing API Activity</h3>
              {indexing_api.recent_logs.length === 0 ? (
                <p className="text-sm text-slate-500 font-body">{indexing_api.configured ? "No activity yet. Publish a job to see indexing events." : "Pending setup."}</p>
              ) : (
                <div className="space-y-1.5">
                  {indexing_api.recent_logs.map((log, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs font-body">
                      {log.status === "success" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" /> :
                       log.status === "error" ? <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" /> :
                       <Clock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                      <span className="text-slate-500 truncate flex-1">{log.url?.replace("https://staffera.eu", "")}</span>
                      <Badge className={`text-[10px] ${log.status === "success" ? "bg-emerald-50 text-emerald-600" : log.status === "error" ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-500"}`}>
                        {log.action} — {log.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-3 mt-3 text-xs text-slate-400 font-body">
                <span>Total: {indexing_api.total_sent}</span>
                <span>Success: {indexing_api.success}</span>
                <span>Errors: {indexing_api.errors}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== OPPORTUNITIES ===== */}
      {section === "opportunities" && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500 font-body">
            {opportunities.length > 0
              ? `${opportunities.length} opportunities detected from ${search_console.configured ? "Search Console + " : ""}real job data.`
              : search_console.configured
                ? "No opportunities detected yet. Publish more jobs and wait for Google data."
                : "Connect Search Console and publish real jobs to detect SEO opportunities."}
          </p>
          {opportunities.map((opp, i) => (
            <Card key={i} className="border-slate-200 bg-white">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Badge className={`text-[10px] mt-0.5 flex-shrink-0 ${opp.priority === "high" ? "bg-red-50 text-red-700 border-red-200" : opp.priority === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600"}`}>
                    {opp.priority}
                  </Badge>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-900 font-body mb-0.5">"{opp.query}"</p>
                    <p className="text-sm text-slate-600 font-body mb-2">{opp.recommendation}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400 font-body">
                      {opp.impressions > 0 && <span><Eye className="w-3 h-3 inline mr-0.5" />{opp.impressions} imp.</span>}
                      {opp.clicks > 0 && <span><MousePointer className="w-3 h-3 inline mr-0.5" />{opp.clicks} clicks</span>}
                      {opp.ctr > 0 && <span>CTR {opp.ctr}%</span>}
                      {opp.position > 0 && <span>Pos. {opp.position}</span>}
                      {opp.related_jobs > 0 && <span><Briefcase className="w-3 h-3 inline mr-0.5" />{opp.related_jobs} jobs</span>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ===== COVERAGE ===== */}
      {section === "coverage" && (
        <div className="space-y-5">
          {/* Summary metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard icon={Briefcase} label="Active jobs" value={coverage.active_jobs} color="text-emerald-600" />
            <MetricCard icon={Globe} label="Countries" value={coverage.countries_with_jobs} />
            <MetricCard icon={MapPin} label="Regions" value={coverage.regions_with_jobs} />
            <MetricCard icon={Users} label="Positions" value={coverage.positions_with_jobs} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard icon={Home} label="With accommodation" value={coverage.accommodation_jobs} />
            <MetricCard icon={Sun} label="Seasonal" value={coverage.seasonal_jobs} />
            <MetricCard icon={Globe} label="Indexable landings" value={landings.indexable} color="text-emerald-600" />
            <MetricCard icon={Globe} label="Noindex landings" value={landings.noindex} color="text-amber-600" />
          </div>

          {/* Countries */}
          {coverage.countries.length > 0 && (
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-5">
                <h3 className="font-sans font-semibold text-sm text-slate-900 mb-3">Countries</h3>
                <div className="space-y-1.5">
                  {coverage.countries.map(c => (
                    <div key={c.iso} className="flex items-center gap-2 text-sm font-body">
                      <StatusDot ok={c.indexable} />
                      <span className="text-slate-700 flex-1">{c.name}</span>
                      <span className="text-slate-400 text-xs">/jobs/{c.slug}</span>
                      <Badge className={`text-xs ${c.indexable ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {c.count} jobs — {c.indexable ? "index" : "noindex"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Positions */}
          {coverage.positions.length > 0 && (
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-5">
                <h3 className="font-sans font-semibold text-sm text-slate-900 mb-3">Positions</h3>
                <div className="space-y-1.5">
                  {coverage.positions.map(p => (
                    <div key={p.id} className="flex items-center gap-2 text-sm font-body">
                      <StatusDot ok={p.indexable} />
                      <span className="text-slate-700 flex-1">{p.label}</span>
                      <span className="text-slate-400 text-xs">/jobs/{p.id}</span>
                      <Badge className={`text-xs ${p.indexable ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {p.count} jobs — {p.indexable ? "index" : "noindex"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Regions */}
          {coverage.regions.length > 0 && (
            <Card className="border-slate-200 bg-white">
              <CardContent className="p-5">
                <h3 className="font-sans font-semibold text-sm text-slate-900 mb-3">Regions</h3>
                <div className="space-y-1.5">
                  {coverage.regions.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm font-body">
                      <StatusDot ok={r.indexable} />
                      <span className="text-slate-700 flex-1">{r.region}, {r.country}</span>
                      <Badge className={`text-xs ${r.indexable ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {r.count} jobs
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {coverage.active_jobs === 0 && (
            <Card className="border-slate-200 bg-white">
              <CardContent className="py-10 text-center">
                <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 font-body">No active jobs. Publish real job offers to activate SEO coverage.</p>
                <p className="text-xs text-slate-400 font-body mt-1">Minimum {landings.threshold} active jobs per country/position to enable an indexable landing page.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
